"use client";

import React, { createContext, useCallback, useContext, useState } from "react";

export interface DialogConfig {
  id: string;
  component: React.ComponentType<Record<string, unknown>>;
  props?: Record<string, unknown>;
  options?: {
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    showCloseButton?: boolean;
    className?: string;
    backgroundStyle?: 'modal' | 'fullscreen';
  };
}

interface DialogContextType {
  dialogs: DialogConfig[];
  openDialog: (config: Omit<DialogConfig, "id">) => string;
  closeDialog: (id: string) => void;
  closeAllDialogs: () => void;
  updateDialog: (id: string, props: Record<string, unknown>) => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialogs, setDialogs] = useState<DialogConfig[]>([]);

  const openDialog = useCallback((config: Omit<DialogConfig, "id">): string => {
    const id = Math.random().toString(36).substr(2, 9);
    const newDialog: DialogConfig = {
      ...config,
      id,
      options: {
        closeOnOverlayClick: true,
        closeOnEscape: true,
        showCloseButton: true,
        ...config.options,
      },
    };

    setDialogs((prev) => [...prev, newDialog]);
    return id;
  }, []);

  const closeDialog = useCallback((id: string) => {
    setDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogs([]);
  }, []);

  const updateDialog = useCallback(
    (id: string, props: Record<string, unknown>) => {
      setDialogs((prev) =>
        prev.map((dialog) =>
          dialog.id === id
            ? { ...dialog, props: { ...dialog.props, ...props } }
            : dialog
        )
      );
    },
    []
  );

  const value: DialogContextType = {
    dialogs,
    openDialog,
    closeDialog,
    closeAllDialogs,
    updateDialog,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      <DialogRenderer />
    </DialogContext.Provider>
  );
}

function DialogRenderer() {
  const { dialogs, closeDialog } = useDialog();

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dialogs.length > 0) {
        const topDialog = dialogs[dialogs.length - 1];
        if (topDialog.options?.closeOnEscape !== false) {
          closeDialog(topDialog.id);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [dialogs, closeDialog]);

  if (dialogs.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9998] overflow-hidden">
      {dialogs.map((dialog, index) => {
        const Component = dialog.component;

        return (
          <div
            key={dialog.id}
            className="fixed inset-0 w-full h-full"
            style={{ zIndex: 9998 + index }}
          >
            {/* Overlay */}
            <div
              className={`absolute inset-0 w-full h-full ${dialog.options?.backgroundStyle === 'fullscreen'
                  ? 'bg-white'
                  : 'bg-[#1C1C1C4D]'
                }`}
              onClick={() => {
                if (dialog.options?.closeOnOverlayClick !== false) {
                  closeDialog(dialog.id);
                }
              }}
            />

            {/* Dialog Content */}
            <div className={`relative z-10 flex ${dialog.options?.backgroundStyle === 'fullscreen'
                ? 'flex-col w-full h-full'
                : 'items-center justify-center w-full h-full p-4'
              }`}>
              {dialog.options?.backgroundStyle === 'fullscreen' ? (
                <>
                  {dialog.options?.showCloseButton !== false && (
                    <div className="flex items-center justify-end px-4 py-6">
                      <button
                        onClick={() => closeDialog(dialog.id)}
                        className="text-xl"
                      >
                        <svg width={15} height={15} viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.8536 2.85355C13.0488 2.65829 13.0488 2.34171 12.8536 2.14645C12.6583 1.95118 12.3417 1.95118 12.1464 2.14645L7.5 6.79289L2.85355 2.14645C2.65829 1.95118 2.34171 1.95118 2.14645 2.14645C1.95118 2.34171 1.95118 2.65829 2.14645 2.85355L6.79289 7.5L2.14645 12.1464C1.95118 12.3417 1.95118 12.6583 2.14645 12.8536C2.34171 13.0488 2.65829 13.0488 2.85355 12.8536L7.5 8.20711L12.1464 12.8536C12.3417 13.0488 12.6583 13.0488 12.8536 12.8536C13.0488 12.6583 13.0488 12.3417 12.8536 12.1464L8.20711 7.5L12.8536 2.85355Z" fill="currentColor" />
                        </svg>
                      </button>
                    </div>
                  )}

                  <div className={`flex-1 px-4 flex flex-col overflow-y-auto ${dialog.options?.className || ""}`}>
                    <Component
                      {...dialog.props}
                      onClose={() => closeDialog(dialog.id)}
                      dialogId={dialog.id}
                    />
                  </div>
                </>
              ) : (
                <div className={`relative ${dialog.options?.className || ""}`}>
                  {dialog.options?.showCloseButton !== false && (
                    <button
                      onClick={() => closeDialog(dialog.id)}
                      className="absolute top-4 right-4 z-10 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 text-md"
                    >
                      ✕
                    </button>
                  )}
                  <Component
                    {...dialog.props}
                    onClose={() => closeDialog(dialog.id)}
                    dialogId={dialog.id}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
}
